// One driver, one month: the declared position, the proven position, and the
// difference between them named as what it is.
//
// THE THREE FIGURES ARE THE WHOLE COMPONENT AND THEY ARE NEVER ADDED.
//
//   Owed on what he declared   what he collected, less what he SAID he did with it
//   Owed on what we can prove  less what Fleetly can SHOW he did with it
//   Evidence we do not hold    the difference, which belongs to us
//
// On the day this was written the third figure was around R113,000 across the
// fleet for August 2026, because 243 declared deposits totalling R116,158 sat
// against R3,000 of cash that reached the account, and no statement had ever been
// loaded so nobody could check. A screen that showed a manager one "validated"
// number that morning would have reported the whole fleet as owing nearly
// everything it collected. The first name on that list is the first name in
// somebody's mouth.
//
// So the proven figure is rendered in muted type beside the declared one, never
// above it, never in the same colour, and never at all in a month whose evidence
// was not loaded: it comes back NULL from the view and shows as "cannot say yet".
import { useState } from 'react'
import { AlertCircle, ShieldQuestion } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { Chip } from '../interviews/Chip'
import { zar, num } from './cashModel'

export type Position = Record<string, any>

// Three of the seven say the money was never missing, and they are listed first
// on purpose. In a business where no bank statement had ever been loaded, those
// three will be the honest answer far more often than the other four.
const OUTCOMES: Array<{ value: string; label: string; help: string }> = [
  {
    value: 'evidence_found',
    label: 'The evidence turned up',
    help: 'A slip, a receipt or a statement line was found. The money was never missing; our record was.',
  },
  {
    value: 'banked_late',
    label: 'It was banked late',
    help: 'It reached the account outside the window we were looking in.',
  },
  {
    value: 'record_corrected',
    label: 'Our record was wrong',
    help: 'The declaration or the Uber figure was wrong and has been corrected.',
  },
  {
    value: 'recovered_in_cash',
    label: 'He handed it over',
    help: 'The cash was received and is now banked.',
  },
  {
    value: 'written_off',
    label: 'Fleetly is not pursuing it',
    help: 'A decision not to chase it, with your name on it. It is not a deduction and it reduces nobody’s salary.',
  },
  {
    value: 'disputed',
    label: 'He disagrees and it is open',
    help: 'Recorded as contested. Nothing is concluded.',
  },
  {
    value: 'referred',
    label: 'It has gone to a disciplinary process',
    help: 'It leaves this screen and is handled there.',
  },
]

function Figure({
  label,
  value,
  hint,
  tone,
}: {
  label: string
  value: string
  hint: string
  tone: 'declared' | 'proven' | 'gap'
}) {
  return (
    <div
      className={
        tone === 'declared'
          ? 'rounded-md border border-border bg-background px-3 py-2'
          : tone === 'proven'
            ? 'rounded-md border border-dashed border-border bg-muted/30 px-3 py-2'
            : 'rounded-md border border-amber-400/50 bg-amber-50 px-3 py-2 dark:bg-amber-950'
      }
    >
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div
        className={
          tone === 'declared'
            ? 'text-lg font-semibold tabular-nums text-foreground'
            : 'text-lg font-semibold tabular-nums text-muted-foreground'
        }
      >
        {value}
      </div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">{hint}</div>
    </div>
  )
}

export function DriverCashPanel({
  position,
  resolutions,
  mayResolve,
  busy,
  onResolve,
}: {
  position: Position
  resolutions: Array<Record<string, any>>
  mayResolve: boolean
  busy: boolean
  onResolve: (args: {
    driver_id: string
    period_month: string
    outcome: string
    amount_zar: number
    reason: string
  }) => void
}) {
  const open = Number(position.open_declared_zar ?? 0)
  const [outcome, setOutcome] = useState(OUTCOMES[0].value)
  const [amount, setAmount] = useState(open > 0 ? open.toFixed(2) : '')
  const [reason, setReason] = useState('')

  const covered = position.statement_covered === true
  const fuelCovered = position.fuel_report_covered === true
  const chosen = OUTCOMES.find((o) => o.value === outcome)

  return (
    <div className="space-y-4 rounded-md border border-border bg-background p-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-sm font-semibold text-foreground">{position.driver_name}</span>
        <span className="text-xs text-muted-foreground">
          {new Date(String(position.shift_month)).toLocaleDateString('en-ZA', {
            month: 'long',
            year: 'numeric',
          })}{' '}
          · {num(position.shifts)} shifts, {num(position.exact_shifts)} with a complete declaration
        </span>
        <Chip tone={covered ? 'green' : 'amber'} className="ml-auto">
          {covered ? 'statement loaded' : 'no statement loaded'}
        </Chip>
        <Chip tone={fuelCovered ? 'green' : 'amber'}>
          {fuelCovered ? 'fuel report loaded' : 'no fuel report loaded'}
        </Chip>
      </div>

      <div className="grid gap-2 sm:grid-cols-3">
        <Figure
          tone="declared"
          label="Owed on what he declared"
          value={zar(position.owed_declared_zar, { signed: true })}
          hint="Cash collected, less what he said he banked, spent on petrol and passed to a team mate."
        />
        <Figure
          tone="proven"
          label="Owed on what we can prove"
          value={
            position.owed_proven_zar === null || position.owed_proven_zar === undefined
              ? 'cannot say yet'
              : zar(position.owed_proven_zar, { signed: true })
          }
          hint={
            covered && fuelCovered
              ? 'Counting only a credit matched on a statement and a fuel transaction on a report.'
              : 'The evidence for this month has not been loaded, so this cannot be computed. It is not zero.'
          }
        />
        <Figure
          tone="gap"
          label="Evidence we do not hold"
          value={
            position.evidence_not_held_zar === null || position.evidence_not_held_zar === undefined
              ? '—'
              : zar(position.evidence_not_held_zar)
          }
          hint="Our unread slips, unloaded statements and missing fuel reports. This is Fleetly's to go and find, and it is not his shortfall."
        />
      </div>

      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <ShieldQuestion className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {position.position_note}
      </p>

      <div className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
        <div>
          <span className="text-muted-foreground">Cash received</span>
          <div className="tabular-nums">{zar(position.cash_received_zar)}</div>
        </div>
        <div>
          <span className="text-muted-foreground">Declared banked</span>
          <div className="tabular-nums">{zar(position.declared_deposited_zar)}</div>
        </div>
        <div>
          <span className="text-muted-foreground">Proven banked</span>
          <div className="tabular-nums text-muted-foreground">
            {position.proven_deposited_zar === null || position.proven_deposited_zar === undefined
              ? 'cannot say yet'
              : zar(position.proven_deposited_zar)}
          </div>
        </div>
        <div>
          <span className="text-muted-foreground">Slips confirmed</span>
          <div className="tabular-nums">
            {num(position.slips_confirmed)} of {num(position.deposit_slips_submitted)} on file
          </div>
        </div>
      </div>

      {resolutions.length > 0 && (
        <div className="rounded-md border border-border">
          <div className="border-b border-border px-3 py-1.5 text-xs font-medium text-foreground">
            Already decided
          </div>
          <ul className="divide-y divide-border">
            {resolutions.map((r) => (
              <li key={String(r.id)} className="px-3 py-2 text-xs">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-medium">
                    {OUTCOMES.find((o) => o.value === r.outcome)?.label ?? String(r.outcome)}
                  </span>
                  <span className="tabular-nums">{zar(r.amount_zar)}</span>
                  <span className="text-muted-foreground">
                    {r.decided_by_name} · {new Date(String(r.decided_at)).toLocaleDateString('en-ZA')}
                  </span>
                  {r.superseded_by && <Chip tone="neutral">superseded</Chip>}
                </div>
                <p className="mt-0.5 text-muted-foreground">{r.reason}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {mayResolve && (
        <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
          <div className="text-xs font-medium text-foreground">Close an item</div>
          <div className="grid gap-2 sm:grid-cols-[2fr_1fr]">
            <div className="space-y-1">
              <Label className="text-[11px]">What happened</Label>
              <NativeSelect value={outcome} disabled={busy} onChange={(e) => setOutcome(e.target.value)}>
                {OUTCOMES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </NativeSelect>
              <p className="text-[11px] text-muted-foreground">{chosen?.help}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Amount it covers</Label>
              <Input
                type="number"
                step="0.01"
                value={amount}
                disabled={busy}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">Why, in your own words</Label>
            <Textarea
              rows={2}
              value={reason}
              disabled={busy}
              placeholder="The next person to read this row has no other way to check it."
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              disabled={busy || reason.trim() === '' || !(Number(amount) >= 0)}
              onClick={() =>
                onResolve({
                  driver_id: String(position.driver_id),
                  period_month: String(position.shift_month),
                  outcome,
                  amount_zar: Number(amount),
                  reason: reason.trim(),
                })
              }
            >
              Record it
            </Button>
            <span className="text-[11px] text-muted-foreground">
              This is a record, not a deduction. It changes no salary and creates no debt.
            </span>
          </div>
        </div>
      )}

      {!covered && (
        <p className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          No bank statement covering this month has been loaded. Before anything here is put to this
          driver, load it on the Bank Statements page: a man cannot answer for a deposit nobody has
          looked for.
        </p>
      )}
    </div>
  )
}
